// frontend/src/components/loi/readLoiInput.ts
import type { LoiGenerateInput } from '../../types';

/** Letter dates are business dates - America/Los_Angeles, never UTC. */
export const laLongDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'long', day: 'numeric' });

/**
 * A date box's day ("2026-09-10") in his long form ("September 10, 2026"), or null when blank. ⭐ LOI-S2 (the 125th): built
 * from the box's own parts in UTC, so the day printed is the day picked on any machine - no clock, no zone.
 */
export const longDateOfDay = (day: string): string | null => {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric',
  });
};

/** The due-diligence lists the builder knows; any other value reads as the standard nine, as it always has. */
const DD_LISTS = ['standard', 'restaurant_pad', 'single_tenant_restaurant', 'vacant'] as const;

/**
 * The form -> the letter's input, exactly as `submit` built it - moved here, behaviour-preserving, so the preview
 * and Generate read the SAME letter. ⛔ A field added to the form is read HERE, or the preview and the filed
 * letter disagree.
 */
export const readLoiInput = (form: HTMLFormElement, now: Date = new Date()): LoiGenerateInput => {
  const fd = new FormData(form);
  const g = (n: string): string => ((fd.get(n) as string) ?? '').trim();
  const num = (n: string): number | undefined => {
    const v = parseFloat(g(n));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  };

  // "5:00 p.m. PST on July 2, 2026" - the corpus's exact deadline form.
  const [ey, em, ed] = g('expiration_date').split('-').map(Number);
  const expiration = ey && em && ed ? `5:00 p.m. PST on ${laLongDate(new Date(ey, em - 1, ed))}` : '';

  const loi: LoiGenerateInput = {
    letterDate: laLongDate(now),
    // ⭐ The 101st - September unless he chose June for a counter to an older letter.
    wording: g('wording') === 'june_2026' ? 'june_2026' : 'september_2026',
    buyers: {
      names: [g('buyer1'), g('buyer2')].filter(Boolean),
      signatureHeading: g('sig_heading') || null,
      profileOverride: g('buyer_profile') || null,
      /**
       * ⛔ The empty option means "use the builder's default", which is the all-cash 1031
       * sentence - NOT `none`. Sending `none` for a blank select would silently delete the
       * Buyer Profile from every letter generated without touching this control.
       */
      buyerProfile:
        (g('buyer_profile_variant') || undefined) as LoiGenerateInput['buyers']['buyerProfile'],
    },
    recipient: {
      sellerName: g('seller_name'),
      salutationName: g('salutation_name') || null,
      // Signing slots follow the named parties: one name, one line.
      sellerSigners: [g('seller_signer1'), g('seller_signer2')].filter(Boolean),
      sellerIsEntity: fd.get('seller_is_entity') === 'on',
      careOf: g('care_of') || null,
      attn: g('attn') || null,
      addressLines: g('address_lines').split('\n').map((l) => l.trim()).filter(Boolean),
    },
    property: {
      address: g('address'),
      city: g('city'),
      state: g('state') || 'CA',
      zip: g('zip') || null,
      buildingSf: num('building_sf') ?? null,
      acres: num('acres') ?? null,
      description: g('description'),
      county: g('county') || null,
      reLineTenant: g('re_line_tenant') || null,
      lotSf: num('lot_sf') ?? null,
      reLineLocation: g('re_line_location') || null,
    },
    terms: {
      purchasePrice: num('price') ?? 0,
      downPayment: num('down_payment') ?? null,
      balanceKind: (g('balance_kind') || 'cash_balance') as LoiGenerateInput['terms']['balanceKind'],
      financingTerms: g('financing_terms').split('\n').map((l) => l.trim()).filter(Boolean),
      priceIncludes: g('price_includes') || null,
      emdAmount: num('emd') ?? 0,
      ddDays: num('dd_days'),
      coeDays: num('coe_days'),
      noLoanContingency: fd.get('no_loan') === 'on',
      escrowCompany: g('escrow_co') || undefined,
      commissionPercent: num('commission'),
      is1031: fd.get('is1031') === 'on',
      downLegCloseDate: g('downleg') || null,
      downLegNotInEscrow: fd.get('downleg_not_in_escrow') === 'on',
      ddList: DD_LISTS.find((l) => l === g('dd_list')) ?? 'standard',
      // ⭐ LOI-S5 (the 127th): his 10-7 terms - each sent as typed, a blank box as null; the builder names a missing half.
      downLegExtensionDays: num('downleg_ext_days') ?? null,
      downLegExtensionFrom: longDateOfDay(g('downleg_ext_from')),
      downLegProtection: fd.get('downleg_protection') === 'on',
      ddFromDeliverables: fd.get('dd_from_deliverables') === 'on',
      coeDaysAfterDd: num('coe_after_dd') ?? null,
      coeOutsideDate: longDateOfDay(g('coe_outside')),
      sellersBrokerage: g('sellers_brokerage') || null,
      expiration,
    },
    /**
     * ⛔ Only sent when the box is ticked AND a number was typed. Sending 0 or NaN would make the
     *   builder REFUSE with a 400 - which is correct, but the dialog should not manufacture the
     *   gap. Absent means an offer, which is the normal case.
     */
    counterToSellerCounterNo:
      fd.get('is_counter') === 'on' ? num('counter_no') ?? null : null,
    // ⭐ LOI-S2 (the 125th): the seller counter's date for his heading - gated by the same tick, optional like the number.
    sellerCounterDate:
      fd.get('is_counter') === 'on' ? longDateOfDay(g('counter_date')) : null,
    // ⭐ LOI-S5: a revised letter - the date is the switch (blank = an ordinary letter), printed as picked.
    revisesLetterDated: longDateOfDay(g('revises_date')),
    revisesRevisedLetter: fd.get('revises_revised') === 'on',
  };
  return loi;
};
